import { React, useEffect, useRef, useState } from "react";
import { AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";
import { BsFillFileEarmarkTextFill, BsX } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { FaFileInvoiceDollar, FaHandHoldingUsd } from "react-icons/fa";
import { FiLogOut, FiSettings } from "react-icons/fi";
import { GiMoneyStack, GiPayMoney } from "react-icons/gi";
import { HiOutlineBriefcase, HiOutlineDocumentText } from "react-icons/hi";
import {
  MdDashboard,
  MdDevices,
  MdGroups,
  MdHomeWork,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdLaptopWindows,
  MdMoney,
  MdWork,
} from "react-icons/md";
import { MdOutlineDashboard } from "react-icons/md";
import { BsFillClockFill } from "react-icons/bs";
import { CiMail } from "react-icons/ci";
import { MdOutlineAttachMoney } from "react-icons/md";
import { useLocation } from "react-router-dom";
// eslint-disable-next-line
import { GrAruba } from "react-icons/gr";
import { CSSTransition } from "react-transition-group";
import { LeftMobNavbar } from "../../StyledCss/mobileNavbar";
import { deleteToken, getMessaging } from "firebase/messaging";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteDeviceToken, getUserData } from "../../api/services/hroneApi";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import {
  closeMobileMenu,
  collapseLeftnavBar,
  expandLeftnavBar,
} from "../../redux/slices/mobileSlice";
import { setUserLoggedOut } from "../../redux/slices/notificationSlice";
import NavBarItems from "../NavBarItems";
import PopupModal from "../PopupModal";

import "./index.css";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import { Users, UserPlus, ShieldCheck } from "lucide-react";
import packageJson from "../../../package.json";
import {
  MODULE_VIEW_PERMISSION,
  MODULES,
  FEATURE_CODES,
  HIRING_MANAGEMENT_FEATURE_CODES,
} from "../../util/featureCodes";

const LeftNavBar = () => {
  const [logoutClicked, setLogoutClicked] = useState(false);

  const location = useLocation();

  const [billingOpen, setBillingOpen] = useState(() => {
    const isBillingRoute =
      location.pathname.startsWith("/revenue") ||
      location.pathname.startsWith("/invoices") ||
      location.pathname.startsWith("/project") ||
      location.pathname.startsWith("/clients") ||
      location.pathname.startsWith("/worklogs");
    return isBillingRoute;
  });

  const [hrOpen, setHrOpen] = useState(() => {
    const isHrRoute =
      location.pathname.startsWith("/hiring") ||
      location.pathname.startsWith("/policies") ||
      location.pathname.startsWith("/form16");
    return isHrRoute;
  });

  const menuClicked = useSelector(
    (state) => state.mobile.isLeftNavBarCollapsed
  );
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const allEmployees = "allEmployees";

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const { primaryColor } = orgDetails;

  const onClickMenu = () => {
    if (menuClicked) {
      dispatch(expandLeftnavBar());
    } else {
      dispatch(collapseLeftnavBar());
    }
  };

  const { empId, role } = getUserData();
  const { hasAnyPermission, hasPermission } = usePermissions();
  // Whether WorkLogs appears at all. Where it appears is decided below by
  // hasOtherBillingModules rather than by role, so both WorkLogs entries can
  // gate on this one permission via their own `permissionCode`.
  const canViewWorklog = hasPermission(FEATURE_CODES.WORKLOG_VIEW);
  // Billing is a group, not a destination — it only earns a place in the nav
  // when it has something to show besides WorkLogs. WorkLogs alone does not
  // count: a user who can see WorkLogs and nothing else billing-related used
  // to get an empty "Billing" dropdown next to a top-level WorkLogs link.
  const hasOtherBillingModules = hasAnyPermission([
    FEATURE_CODES.REVENUE_VIEW,
    FEATURE_CODES.INVOICE_VIEW,
    FEATURE_CODES.PROJECT_VIEW,
    FEATURE_CODES.CLIENT_VIEW,
  ]);
  // WorkLogs therefore lives in one of two places, never both: inside Billing
  // when that group exists, top-level when it doesn't.
  const showWorklogUnderBilling = canViewWorklog && hasOtherBillingModules;
  const showWorklogAtTopLevel = canViewWorklog && !hasOtherBillingModules;
  // Project-scoped WorkLogs for whoever can see everyone else's logs;
  // own-logs view otherwise. Permission-gated (WORKLOG_VIEW_ALL) rather than
  // role-gated so it follows the same role-permission config as the rest of
  // the app instead of a hardcoded role list.
  const worklogPath = hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL)
    ? "/worklogs_project"
    : "/worklogs";
  // Two Hiring UIs, same as WorkLogs: the HR management console for roles with
  // any Hiring management permission, the employee job-openings screen for
  // roles that only have the default Hiring access.
  const hiringPath = hasAnyPermission(HIRING_MANAGEMENT_FEATURE_CODES)
    ? "/hiring"
    : "/hiring/openings";

  const billingItems = [
    {
      id: 2,
      icon: <MdMoney />,
      navItemName: "Revenue",
      path: "/revenue",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.REVENUE_VIEW,
    },
    {
      id: 3,
      icon: <MdOutlineAttachMoney />,
      navItemName: "Invoices",
      path: "/invoices",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.INVOICE_VIEW,
    },
    {
      id: 13,
      icon: <MdOutlineDashboard />,
      navItemName: "Projects",
      path: "/projects",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.PROJECT_VIEW,
    },
    {
      id: 14,
      icon: <Users />,
      navItemName: "Clients",
      path: "/clients",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.CLIENT_VIEW,
    },
    ...(showWorklogUnderBilling
      ? [
          {
            id: 15,
            icon: <BsFillClockFill />,
            navItemName: "WorkLogs",
            path: worklogPath,
            permissionCode: FEATURE_CODES.WORKLOG_VIEW,
          },
        ]
      : []),
  ];

  const hrItems = [
    // The HR-console "Hiring" entry uses a permissionCode, which bypasses the
    // roles list entirely (see isModuleAccessible) — so it must be excluded
    // outright for Employee/Lead, who have their own "/hiring/openings" entry
    // below, to avoid showing "Hiring" twice once they're granted hiring.view.
    ...(["Employee", "Lead"].includes(role)
      ? []
      : [
          {
            id: 20,
            icon: <UserPlus />,
            navItemName: "Hiring",
            path: hiringPath,
            roles: ["Super Admin", "Admin", "HR"],
            permissionCode: FEATURE_CODES.HIRING_VIEW,
          },
        ]),
    {
      id: 21,
      icon: <UserPlus />,
      navItemName: "Hiring",
      path: "/hiring/openings",
      roles: ["Employee", "Lead"],
    },
    {
      id: 11,
      icon: <BsFillFileEarmarkTextFill />,
      navItemName: "Policies",
      path: "/policies",
      roles: ["Super Admin", "Admin", "HR", "Employee", "Lead"],
      permissionCode: FEATURE_CODES.POLICY_VIEW,
    },
    {
      id: 19,
      icon: <HiOutlineDocumentText />,
      navItemName: "Form 16",
      path: "/form16",
      roles: ["Super Admin", "Admin", "HR", "Employee", "Lead"],
      permissionCode: FEATURE_CODES.FORM16_VIEW,
    },
  ];

  // Standalone nav item rendered right after the HR dropdown group.
  const rolePermissionsItem = {
    id: 22,
    icon: <ShieldCheck />,
    navItemName: "Role Permissions",
    path: "/role_permissions",
    roles: ["Super Admin", "Admin"],
  };

  const navItems = [
    {
      id: 1,
      icon: <MdDashboard />,
      navItemName: "Dashboard",
      path: "/",
      roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
      permissionCode: MODULE_VIEW_PERMISSION[MODULES.DASHBOARD],
    },

    {
      id: 4,
      icon: <MdGroups />,
      navItemName: "Employees",
      path: "/allemployees",
      roles: ["Super Admin", "Admin", "HR"],
      permissionCode: MODULE_VIEW_PERMISSION[MODULES.EMPLOYEE],
    },
    {
      id: 5,
      icon: <HiOutlineBriefcase />,
      navItemName: "Leave Management",
      path: "/leave_management",
      roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
      permissionCode: FEATURE_CODES.LEAVE_VIEW_MODULE,
    },

    {
      id: 6,
      icon: <FaHandHoldingUsd />,
      navItemName: "Salary Management",
      path: "/salary",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.SALARY_VIEW,
    },
    {
      id: 7,
      icon: <FaFileInvoiceDollar />,
      navItemName: "Payslip",
      path: `/payslip/${empId}`,
      roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
      permissionCode: FEATURE_CODES.PAYSLIP_VIEW,
    },
    {
      id: 8,
      icon: <MdDevices />,
      navItemName: "Resource Tracking",
      path: "/resource_tracking",
      roles: ["Super Admin", "Admin", "HR"],
      permissionCode: FEATURE_CODES.RESOURCE_VIEW,
    },

    {
      id: 9,
      icon: <GiPayMoney />,
      navItemName: "Reimbursement",
      path: "/reimbursement",
      roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
      permissionCode: FEATURE_CODES.REIMBURSEMENT_VIEW,
    },
    {
      id: 10,
      icon: <GiMoneyStack />,
      navItemName: "Expenditure",
      path: "/expenditure",
      roles: ["Super Admin", "Admin", "HR"],
      permissionCode: FEATURE_CODES.EXPENDITURE_VIEW,
    },
    {
      id: 12,
      icon: <MdHomeWork />,
      navItemName: "Tenants",
      path: "/tenant",
      roles: ["Super Admin"],
    },
     ...(showWorklogAtTopLevel
      ? [
          {
            id: 18,
            icon: <BsFillClockFill />,
            navItemName: "WorkLogs",
            path: worklogPath,
            permissionCode: FEATURE_CODES.WORKLOG_VIEW,
          },
        ]
      : []),
     {
      id: 14,
      icon: <CiMail />,
      navItemName: "Email Templates",
      path: "/email_templates",
      roles: [ "Admin", "HR"],
      permissionCode: FEATURE_CODES.EMAIL_TEMPLATE_VIEW,
    },
  ];

  const bottomNavItems = [
    {
      id: 15,
      icon: <CgProfile />,
      navItemName: "Your Profile",
      path: "/profile",
      roles: ["Super Admin", "Admin", "Employee", "HR", "Lead"],
      permissionCode: FEATURE_CODES.PROFILE_VIEW,
    },
    

    {
      id: 17,
      icon: <FiSettings />,
      navItemName: "Settings",
      path: "/settings",
      roles: ["Super Admin", "Admin"],
      permissionCode: FEATURE_CODES.SETTINGS_VIEW,
    },
  ];

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKeyClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKeyClick);
    }; // eslint-disable-next-line
  }, []);

  const handleEscapeKeyClick = (event) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target) &&
      event.key === "Escape"
    ) {
      closeModal();
    }
  };

  const handleOutsideClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeModal();
    }
  };

  const navigate = useNavigate();

  const onClickLogout = () => {
    try {
      const devToken = JSON.parse(localStorage.getItem("devToken"));
      if (devToken) {
        const messaging = getMessaging();
        deleteToken(messaging)
          .then(() => {})
          .catch((err) => {
            console.log("Unable to delete token. ", err);
          });

        deleteDeviceToken(devToken);
      }
    } catch (err) {
      console.log(err);
    }

    localStorage.removeItem("loginDetails");
    localStorage.removeItem("devToken");
    localStorage.removeItem("org");
    dispatch(setUserLoggedOut());
    navigate("/login");
  };

  const isModalOpen = useSelector((state) => state.mobile.isMobileMenu);

  const closeModal = () => {
    dispatch(closeMobileMenu());
  };

  const closeLogoutModal = () => {
    setLogoutClicked(false);
  };

  const toggleBilling = () => {
    setBillingOpen(!billingOpen);
  };

  const toggleHr = () => {
    setHrOpen(!hrOpen);
  };

  const navBarWidth = !menuClicked ? "expand-menu" : "collapse-menu";

  return (
    <div className="left-navigation-container">
      <nav
        style={{ backgroundColor: `${primaryColor}` }}
        className={`left-nav-bar large ${navBarWidth}`}
      >
        <ul className="nav-items-container">
          <li
            style={{ marginLeft: !menuClicked ? "200px" : "5px" }}
            className="hamburger-icon"
            onClick={onClickMenu}
          >
            {!menuClicked ? <AiOutlineDoubleLeft /> : <AiOutlineDoubleRight />}
          </li>
          {navItems.map((eachItem, index) => (
            <NavBarItems
              key={index}
              navitemDetails={eachItem}
              allEmployees={eachItem.navItemName === allEmployees}
              closeModal={closeModal}
            />
          ))}

          {/* HR Dropdown — visible to every role; each sub-item still
              self-filters by its own roles list inside NavBarItems. */}
          <div className="dropdown-nav-item">
            <li
              onClick={toggleHr}
              className="d-flex nav-item-container align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex align-items-center">
                <div className="nav-item-icon">
                  <MdWork />
                </div>
                <p style={{ fontSize: "16px" }} className="mt-4">
                  HR
                </p>
              </div>
              <div className="dropdown-arrow">
                {hrOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
              </div>
            </li>

            {hrOpen && (
              <ul className="dropdown-submenu" style={{ paddingLeft: "20px" }}>
                {hrItems.map((subItem) => (
                  <NavBarItems
                    key={subItem.id}
                    navitemDetails={subItem}
                    closeModal={closeModal}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Billing Dropdown — visible only when the user holds a billing view
              permission other than WorkLogs, so the group can never render
              empty; each sub-item still self-filters via NavBarItems. When it
              is hidden, WorkLogs moves to the top-level nav instead. */}
          {hasOtherBillingModules && (
            <div className="dropdown-nav-item">
              <li
                onClick={toggleBilling}
                className="d-flex nav-item-container align-items-center justify-content-between"
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex align-items-center">
                  <div className="nav-item-icon">
                    <MdLaptopWindows />
                  </div>
                  <p style={{ fontSize: "16px" }} className="mt-4">
                    Billing
                  </p>
                </div>
                <div className="dropdown-arrow">
                  {billingOpen ? (
                    <MdKeyboardArrowUp />
                  ) : (
                    <MdKeyboardArrowDown />
                  )}
                </div>
              </li>

              {billingOpen && (
                <ul
                  className="dropdown-submenu"
                  style={{ paddingLeft: "20px" }}
                >
                  {billingItems.map((subItem) => (
                    <NavBarItems
                      key={subItem.id}
                      navitemDetails={subItem}
                      closeModal={closeModal}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Role Permissions — standalone item placed below the Billing group */}
          <NavBarItems
            navitemDetails={rolePermissionsItem}
            closeModal={closeModal}
          />
        </ul>

        <hr className="" />
        <ul className="bottom-nav-container">
          {bottomNavItems.map((eachItem) => (
            <NavBarItems
              closeModal={closeModal}
              allEmployees={eachItem.navItemName === allEmployees}
              key={eachItem.id}
              navitemDetails={eachItem}
            />
          ))}
          <li
            // onClick={onClickLogout}
            onClick={() => setLogoutClicked(true)}
            className="d-flex align-items logout-list-item mt-2"
          >
            <div>
              <FiLogOut className="nav-icons " />
            </div>

            <p className="logout-btn-nav">Logout</p>
          </li>
        </ul>

        {/* App Version */}
        <div className="app-version">
          v{packageJson.version}
        </div>
      </nav>

      <PopupModal
        closeBtn={closeLogoutModal}
        modalName="Logout"
        showModal={logoutClicked}
      >
        <div className="logout-modal-container">
          <p style={{ fontSize: "14px" }}>Are you sure want to logout?</p>
          <div className="d-flex gap-2">
            <SecondaryButton onClick={() => setLogoutClicked(false)}>
              No
            </SecondaryButton>
            <PrimaryButton onClick={onClickLogout}>Yes</PrimaryButton>
          </div>
        </div>
      </PopupModal>

      {/* mobileNavbar */}
      <CSSTransition
        in={isModalOpen}
        timeout={300}
        classNames="slideMobileMenu"
        unmountOnExit
      >
        <LeftMobNavbar ref={modalRef} className="mobile-menu">
          <div className="close-menu-div">
            <button
              className="mobile-menu-close-icon"
              onClick={closeModal}
              type="button"
            >
              <BsX />
            </button>
          </div>
          <nav className="">
            <ul className="nav-items-container">
              {navItems.map((eachItem, index) => (
                <NavBarItems
                  key={index}
                  navitemDetails={eachItem}
                  allEmployees={eachItem.navItemName === allEmployees}
                />
              ))}

              {/* HR Dropdown — mirrors the desktop nav's grouped section */}
              <div className="dropdown-nav-item">
                <li
                  onClick={toggleHr}
                  className="d-flex nav-item-container align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center">
                    <div className="nav-item-icon">
                      <MdWork />
                    </div>
                    <p style={{ fontSize: "16px" }} className="mt-4">
                      HR
                    </p>
                  </div>
                  <div className="dropdown-arrow">
                    {hrOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                  </div>
                </li>

                {hrOpen && (
                  <ul
                    className="dropdown-submenu"
                    style={{ paddingLeft: "20px" }}
                  >
                    {hrItems.map((subItem) => (
                      <NavBarItems
                        key={subItem.id}
                        navitemDetails={subItem}
                        closeModal={closeModal}
                      />
                    ))}
                  </ul>
                )}
              </div>

              {/* Role Permissions — standalone item placed after the HR group */}
              <NavBarItems
                navitemDetails={rolePermissionsItem}
                closeModal={closeModal}
              />
            </ul>
            <ul className="logout-container">
              <hr className="" />
              {bottomNavItems.map((eachItem) => (
                <NavBarItems
                  key={eachItem.id}
                  navitemDetails={eachItem}
                  closeModal={closeModal}
                />
              ))}
              <li
                onClick={onClickLogout}
                className="d-flex align-items logout-list-item"
              >
                <div className="nav-item-icon">
                  <FiLogOut className="logout-icon" />
                </div>
                <p className="logout-btn-nav">Logout</p>
              </li>
            </ul>

            {/* App Version */}
            <div className="app-version">
              v{packageJson.version}
            </div>
          </nav>
        </LeftMobNavbar>
      </CSSTransition>
    </div>
  );
};

export default LeftNavBar;
